const WHITE = "#ffffff";
const BLACK = "#000000";
const DEFAULT_THEME = "#2fbaad";
const DEFAULT_SIDEBAR_COLOR = "#ffffff";

function normalizeHex(color) {
  if (!color) {
    return null;
  }
  const value = String(color).trim();
  if (!value.startsWith("#")) {
    return null;
  }
  let hex = value.slice(1);
  if (hex.length === 3) {
    hex = hex.split("").map(char => char + char).join("");
  }
  if (hex.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(hex)) {
    return null;
  }
  return `#${hex.toLowerCase()}`;
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex) || normalizeHex(DEFAULT_THEME);
  const int = parseInt(normalized.slice(1), 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
}

function relativeLuminance({ r, g, b }) {
  const toLinear = channel => {
    const srgb = channel / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  return (
    0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
  );
}

/** True when the color reads as a light background (WCAG relative luminance). */
export function isLightColor(color) {
  return relativeLuminance(hexToRgb(color)) > 0.5;
}

/** True only when primary branding is exactly white (#fff / #ffffff). */
export function isWhitePrimary(themeColor) {
  const normalized = normalizeHex(themeColor);
  if (normalized === WHITE) {
    return true;
  }
  const raw = String(themeColor || "").trim().toLowerCase();
  return raw === "white" || raw === "fff" || raw === "ffffff";
}

/** Contrast tokens used by previews — only meaningful when primary is white. */
export function getThemeContrastColors(themeColor) {
  const whitePrimary = isWhitePrimary(themeColor);
  const theme = normalizeHex(themeColor) || normalizeHex(DEFAULT_THEME);
  return {
    onTheme: whitePrimary ? BLACK : WHITE,
    accent: whitePrimary ? BLACK : theme,
    border: whitePrimary ? BLACK : theme,
  };
}

/**
 * Icon/text on a branded surface:
 * - Light background + different from theme → primary
 * - Dark/medium background → white
 * - Light background + same as theme → black
 */
export function getBrandingForegroundColor(backgroundColor, themeColor) {
  const primary = normalizeHex(themeColor) || normalizeHex(DEFAULT_THEME);
  const backgroundNormalized = normalizeHex(backgroundColor) || primary;
  const backgroundIsLight = isLightColor(backgroundColor);
  const whitePrimary = isWhitePrimary(themeColor);

  if (backgroundNormalized !== primary) {
    if (whitePrimary && backgroundIsLight) {
      return BLACK;
    }
    return backgroundIsLight ? primary : WHITE;
  }

  return backgroundIsLight ? BLACK : WHITE;
}

/** @deprecated Use getBrandingForegroundColor */
export function getSidebarForegroundColor(sidebarColor, themeColor) {
  return getBrandingForegroundColor(sidebarColor, themeColor);
}

function surfaceTextVars(prefix, backgroundColor, themeColor) {
  const textColor = getBrandingForegroundColor(backgroundColor, themeColor);
  const { r, g, b } = hexToRgb(textColor);
  return {
    [`--branding-${prefix}-text-color`]: textColor,
    [`--branding-${prefix}-text-muted`]: `rgba(${r}, ${g}, ${b}, 0.92)`,
  };
}

/**
 * Sidebar + topbar CSS vars. Same rule on each surface, using that surface's background:
 * - Light + differs from theme → primary
 * - Dark/medium → white
 * - Light + same as theme → black
 * Hover: primary fill when sidebar differs from theme; subtle overlay when they match.
 */
export function getSidebarBrandingCssVars(sidebarColor, themeColor) {
  const primary = normalizeHex(themeColor) || normalizeHex(DEFAULT_THEME);
  const sidebarNormalized = normalizeHex(sidebarColor) || normalizeHex(DEFAULT_SIDEBAR_COLOR);
  const sidebarIsLight = isLightColor(sidebarColor);
  const sidebarDiffersFromTheme = sidebarNormalized !== primary;
  const sidebarTextColor = getBrandingForegroundColor(sidebarColor, themeColor);
  const whitePrimary = isWhitePrimary(themeColor);

  let hoverBg;
  let hoverBorder;
  let hoverTextColor;

  if (sidebarDiffersFromTheme) {
    if (whitePrimary) {
      hoverBg = "rgba(0, 0, 0, 0.08)";
      hoverBorder = "rgba(0, 0, 0, 0.18)";
      hoverTextColor = BLACK;
    } else {
      hoverBg = primary;
      hoverBorder = primary;
      hoverTextColor = WHITE;
    }
  } else {
    hoverBg = sidebarIsLight ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)";
    hoverBorder = sidebarIsLight ? "rgba(0, 0, 0, 0.18)" : "rgba(255, 255, 255, 0.18)";
    hoverTextColor = sidebarTextColor;
  }

  return {
    ...surfaceTextVars("sidebar", sidebarColor, themeColor),
    ...surfaceTextVars("topbar", themeColor, themeColor),
    "--branding-sidebar-hover-bg": hoverBg,
    "--branding-sidebar-hover-border": hoverBorder,
    "--branding-sidebar-hover-text-color": hoverTextColor,
  };
}

/** Apply branding CSS variables on an element (preview iframe only). */
export function applyBrandingCssVars(element, { themeColor, sidebarColor } = {}) {
  if (!element?.style) {
    return;
  }
  const theme = normalizeHex(themeColor) || themeColor || DEFAULT_THEME;
  const sidebar = normalizeHex(sidebarColor) || sidebarColor || DEFAULT_SIDEBAR_COLOR;
  const whitePrimary = isWhitePrimary(theme);

  if (element.setAttribute) {
    if (whitePrimary) {
      element.setAttribute("data-white-primary", "true");
    } else {
      element.removeAttribute("data-white-primary");
    }
  }

  const vars = {
    "--branding-theme-color": theme,
    "--theme-color": theme,
    "--skin1": theme,
    "--branding-sidebar-color": sidebar,
    ...getSidebarBrandingCssVars(sidebar, theme),
  };
  Object.entries(vars).forEach(([name, value]) => {
    element.style.setProperty(name, value);
  });
}
